import Colors from '@constants/Colors';
import { useDownloadFile } from '@hooks/useDownloadFile';
import { Button, CircularProgress, Typography } from '@mui/material';
import { CSSProperties, FC, useCallback, useState } from 'react';
import { readableFileSize } from '.';
import FilePreviewDialog from './FilePreviewDialog';

interface DownloadFileProps {
    file: {
        _id?: string;
        name: string;
        size?: number;
        url?: string;
    };
    style?: CSSProperties;
    textStyle?: CSSProperties;
    hideFileSize?: boolean;
    action?: 'preview' | 'download';
}

const PreviewOrDownloadFileButton: FC<DownloadFileProps> = ({
    file,
    style,
    textStyle,
    hideFileSize = false,
    action = 'download',
}) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number>(0);

    const { download, downloadFromUrl, loading } = useDownloadFile({
        onSuccess: url => {
            if (action === 'preview') {
                setPreviewUrl(url);
                setPreviewOpen(true);
            } else {
                downloadFromUrl(url, file.name);
            }
        },
    });

    const handleClick = useCallback(() => {
        download(file);
    }, [file, download]);

    const handleDownloadFromPreview = useCallback(() => {
        if (previewUrl) {
            downloadFromUrl(previewUrl, file.name);
        } else if (file.url?.includes('cloudinary')) {
            downloadFromUrl(file.url, file.name);
        } else {
            download(file);
        }
    }, [previewUrl, file, downloadFromUrl, download]);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setPreviewUrl(null);
        setNumPages(0);
    };

    return (
        <>
            <Button
                variant="text"
                sx={{ borderRadius: 0, padding: 0, margin: 0 }}
                style={style}
                disabled={loading || !file._id}
                onClick={handleClick}
                className="download-Button"
                endIcon={
                    loading ? (
                        <CircularProgress size={12} />
                    ) : file._id ? (
                        <i
                            className="fi fi-br-eye"
                            style={{
                                fontSize: '16px',
                                color: Colors.NEUTRAL_DARK_GRAY,
                                paddingRight: 4,
                                ...textStyle,
                            }}
                        />
                    ) : null
                }
            >
                <Typography fontSize={14} style={textStyle}>
                    {file.name}
                    {!hideFileSize && (
                        <Typography
                            component="span"
                            fontSize={14}
                            style={{ marginTop: 8 }}
                            color={Colors.NEUTRAL_DARK_GRAY}
                        >
                            {' '}
                            ({readableFileSize(file.size)})
                        </Typography>
                    )}
                </Typography>
            </Button>

            {previewUrl && (
                <FilePreviewDialog
                    open={previewOpen}
                    onClose={handleClosePreview}
                    file={{ name: file.name, url: previewUrl }}
                    onDownload={action === 'preview' ? handleDownloadFromPreview : undefined}
                    showDownloadButton={action === 'preview'}
                    numPages={numPages}
                    onDocumentLoadSuccess={onDocumentLoadSuccess}
                />
            )}
        </>
    );
};

export default PreviewOrDownloadFileButton;
